import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ArrowUpDown, Copy, Eye, EyeOff, Lock, MoreHorizontal, Pin, Plus, Share2, SlidersHorizontal, Trash2,
} from '../../../icons';
import { useAnchoredMenu } from '../../../hooks/useAnchoredMenu';
import {
  useDashboardStore, BOARD_VISIBILITY_LABEL, boardColorToken, type DashboardBoard,
} from '../../../store/dashboardStore';
// 대시보드 시안 v2 V5 (2026-09-16) — 깃발이 켜지면 기본 표시가 핀 아이콘 대신 「기본」 글자. 끄면 종전 그대로.
import { DASHBOARD_V2_ENABLED } from '../../../lib/layout-shell';

/**
 * 대시보드 탭 줄 (2026-09-08 · 시안 화면 H).
 *
 * 탭마다 ⋯ 메뉴 — 이름 변경 · 대시보드 설정 · 복제 · 기본으로 지정 · 탭에서 숨기기 · 삭제.
 * 「탭 순서 바꾸기」로 들어가면 탭을 끌어 옮긴다.
 *
 * ★★★ **숨기기는 지우기가 아니다.** 되살릴 길을 함께 두지 않으면 사실상 삭제다 —
 *     그래서 탭 줄 끝에 **「숨긴 탭 N」**을 둔다(시안에는 없다. 없으면 «할 수 없는 일»을
 *     메뉴에 세우는 셈이 되어 넣었다).
 *
 * ★★ 순서와 숨김은 **나에게만 적용된다.** 남의 탭 줄을 바꾸는 일이 아니다(시안 곁말 그대로).
 *    회사가 「맨 앞 고정」·「숨기기 잠금」을 걸면(migration 175) 그 조작이 통째로 잠긴다.
 *
 * ★ 탭 순서 끌기는 HTML5 DnD 가 아니라 포인터로 한다 — 탭은 <button> 이라 끌리는 중에도
 *   누름 판정이 살아 있어야 하고(놓으면 그 탭이 열린다), 터치까지 한 벌로 처리된다.
 *
 * ⚠⚠⚠ **메뉴는 `createPortal(document.body)` 로 띄운다 (2026-09-09 · 육안 지적).**
 *   탭 줄은 `overflow-x: auto` 다(탭이 많으면 옆으로 밀려야 한다). 그런데 **한 축이 auto 면
 *   나머지 축도 auto** 라, 탭 줄 «안» 에 `position:absolute` 로 그린 메뉴는 **세로로 잘려**
 *   아무것도 안 보인다 — 「⋯ 을 눌러도 설정이 안 나온다」의 정체가 이것이었다.
 *   시안(화면 H)의 탭 줄에는 `overflow` 가 없어 이 함정이 안 보였다.
 *   같은 이유로 「숨긴 탭」 목록도 포털로 띄운다.
 */

interface Props {
  boards: DashboardBoard[];
  activeBoardId: string;
  onCreate: () => void;
  onSettings: (board: DashboardBoard) => void;
  onDelete: (board: DashboardBoard) => void;
}

export function DashboardTabs({ boards, activeBoardId, onCreate, onSettings, onDelete }: Props) {
  const hiddenBoardIds = useDashboardStore((s) => s.hiddenBoardIds);
  const defaultBoardId = useDashboardStore((s) => s.defaultBoardId);
  const canHideTabs = useDashboardStore((s) => s.canHideTabs);
  const canReorderTabs = useDashboardStore((s) => s.canReorderTabs);
  const setActiveBoard = useDashboardStore((s) => s.setActiveBoard);
  const hideBoard = useDashboardStore((s) => s.hideBoard);
  const unhideBoard = useDashboardStore((s) => s.unhideBoard);
  const reorderBoards = useDashboardStore((s) => s.reorderBoards);
  const duplicateBoard = useDashboardStore((s) => s.duplicateBoard);
  const setDefaultBoard = useDashboardStore((s) => s.setDefaultBoard);

  const [menuFor, setMenuFor] = useState<string | null>(null);
  /* 메뉴 상자 어림값 — `useAnchoredMenu` 가 첫 좌표를 잡을 때 쓴다(CSS 와 함께 고친다). */
  const tabMenu = useAnchoredMenu<HTMLButtonElement>({ width: 200, height: 292 }, { align: 'start' });
  const hiddenMenu = useAnchoredMenu<HTMLButtonElement>({ width: 200, height: 180 }, { align: 'start' });
  const [sorting, setSorting] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const dragId = useRef<string | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);

  const visible = boards.filter((b) => !hiddenBoardIds.includes(b.id));
  const hidden = boards.filter((b) => hiddenBoardIds.includes(b.id));

  const indexOf = (id: string) => boards.findIndex((b) => b.id === id);

  /** 메뉴를 닫는다 — 열림 표시와 좌표를 **함께** 버린다(하나만 버리면 다음에 안 열린다). */
  const closeTabMenu = () => { setMenuFor(null); tabMenu.close(); };

  const onPointerDown = (id: string) => (e: React.PointerEvent) => {
    if (!sorting) return;
    dragId.current = id;
    barRef.current?.setPointerCapture(e.pointerId);
    e.preventDefault();
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragId.current) return;
    // 캡처 중에는 pointerenter 가 안 오므로 좌표로 대상을 직접 찾는다.
    const over = document.elementFromPoint(e.clientX, e.clientY);
    const target = over?.closest?.('[data-tab-id]') as HTMLElement | null;
    const overId = target?.dataset.tabId;
    if (!overId || overId === dragId.current) return;
    reorderBoards(indexOf(dragId.current), indexOf(overId));
  };
  const endDrag = () => { dragId.current = null; };

  return (
    <div
      className="dw-tabs"
      role="tablist"
      aria-label="대시보드 전환"
      ref={barRef}
      data-sorting={sorting || undefined}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      {visible.map((b) => {
        const active = b.id === activeBoardId;
        const isDefault = b.id === defaultBoardId;
        return (
          <span
            className={`dwx-tabwrap${active ? ' is-active' : ''}`}
            key={b.id} data-tab-id={b.id} onPointerDown={onPointerDown(b.id)}
          >
            <button
              type="button"
              className={`dw-tab${active ? ' is-active' : ''}`}
              role="tab"
              aria-selected={active}
              onClick={() => { if (!sorting) setActiveBoard(b.id); }}
            >
              {/* ★ 고른 색이 있으면 그것이 이긴다 — 없으면 종전대로 종류 색(회귀 0). */}
              <span
                className={`dw-tab__dot dw-tab__dot--${b.kind}`}
                style={boardColorToken(b.color) ? { background: boardColorToken(b.color)! } : undefined}
                aria-hidden
              />
              {b.name}
              {isDefault && (DASHBOARD_V2_ENABLED
                ? <>{' '}<em className="dwv-tab__def">기본</em></>
                : <Pin size={11} aria-label="기본 대시보드" className="dwx-pin" />)}
              {b.locked && <Lock size={11} aria-label="편집 잠금" />}
            </button>
            {active && !sorting && (
              <button
                type="button"
                ref={tabMenu.anchorRef}
                className="dw-icon-btn dwx-tabmore"
                aria-label={`${b.name} 메뉴`}
                aria-haspopup="menu"
                aria-expanded={menuFor === b.id}
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuFor((cur) => (cur === b.id ? null : b.id));
                  tabMenu.toggle();
                }}
              >
                <MoreHorizontal size={12} aria-hidden />
              </button>
            )}
            {menuFor === b.id && tabMenu.pos && createPortal(
              <>
                <button type="button" className="dw-menu__scrim" aria-label="메뉴 닫기"
                  onClick={() => { setMenuFor(null); tabMenu.close(); }} />
                <div
                  className="dw-menu dw-menu--wide dw-menu--fixed" role="menu"
                  aria-label={`${b.name} 메뉴`}
                  style={{ top: tabMenu.pos.top, left: tabMenu.pos.left }}
                >
                  {/* ★★★ 「이름 변경」·「공유 범위」·「대시보드 설정」이 **셋 다 같은 창**을 열고
                        있었다(2026-09-09 사용자 지적) — 한 줄로 줄이고 창이 다 갖게 했다.
                        여기서는 공개 범위를 **보여 주기만** 한다(정하는 곳은 그 창 한 곳). */}
                  <button type="button" role="menuitem" className="dw-menu__item"
                    onClick={() => { closeTabMenu(); onSettings(b); }}>
                    <SlidersHorizontal size={13} aria-hidden />대시보드 설정
                    <span className="k">{BOARD_VISIBILITY_LABEL[b.visibility]}</span>
                  </button>
                  <button type="button" role="menuitem" className="dw-menu__item"
                    onClick={() => { duplicateBoard(b.id); closeTabMenu(); }}>
                    <Copy size={13} aria-hidden />복제
                  </button>
                  <button type="button" role="menuitem" className={`dw-menu__item${isDefault ? ' is-on' : ''}`}
                    onClick={() => { setDefaultBoard(b.id); closeTabMenu(); }}>
                    <Pin size={13} aria-hidden />{isDefault ? '기본 해제' : '기본으로 지정'}
                  </button>
                  <div className="dw-menu__sep" />
                  <button type="button" role="menuitem" className="dw-menu__item"
                    disabled={!canReorderTabs}
                    onClick={() => { setSorting(true); closeTabMenu(); }}>
                    <ArrowUpDown size={13} aria-hidden />탭 순서 바꾸기
                    {!canReorderTabs && <span className="k">잠김</span>}
                  </button>
                  <button type="button" role="menuitem" className="dw-menu__item"
                    disabled={!canHideTabs || visible.length < 2}
                    onClick={() => { hideBoard(b.id); closeTabMenu(); }}>
                    <EyeOff size={13} aria-hidden />탭에서 숨기기
                    {!canHideTabs && <span className="k">잠김</span>}
                  </button>
                  {!b.locked && (
                    <>
                      <div className="dw-menu__sep" />
                      <button type="button" role="menuitem" className="dw-menu__item dw-menu__item--danger"
                        onClick={() => { closeTabMenu(); onDelete(b); }}>
                        <Trash2 size={13} aria-hidden />대시보드 삭제
                      </button>
                    </>
                  )}
                  {b.locked && (
                    <div className="dw-menu__label">
                      <Share2 size={12} aria-hidden />회사 담당자가 관리합니다
                    </div>
                  )}
                </div>
              </>,
              document.body,
            )}
          </span>
        );
      })}

      <button type="button" className="dw-tab dw-tab--add" onClick={onCreate} aria-label="대시보드 추가">
        <Plus size={15} aria-hidden />
      </button>

      {/* ★★★ 숨긴 것을 되살릴 자리. 이것이 없으면 「숨기기」는 사실상 삭제다. */}
      {hidden.length > 0 && (
        <span className="dwx-tabwrap">
          <button type="button" ref={hiddenMenu.anchorRef} className="dw-tab dwx-hiddenbtn"
            aria-haspopup="menu" aria-expanded={showHidden}
            onClick={() => { setShowHidden((v) => !v); hiddenMenu.toggle(); }}>
            <Eye size={13} aria-hidden />숨긴 탭 {hidden.length}
          </button>
          {showHidden && hiddenMenu.pos && createPortal(
            <>
              <button type="button" className="dw-menu__scrim" aria-label="닫기"
                onClick={() => { setShowHidden(false); hiddenMenu.close(); }} />
              <div className="dw-menu dw-menu--wide dw-menu--fixed" role="menu" aria-label="숨긴 탭"
                style={{ top: hiddenMenu.pos.top, left: hiddenMenu.pos.left }}>
                <div className="dw-menu__label">다시 보이게 하려면 누르세요</div>
                {hidden.map((b) => (
                  <button key={b.id} type="button" role="menuitem" className="dw-menu__item"
                    onClick={() => { unhideBoard(b.id); setShowHidden(false); hiddenMenu.close(); }}>
                    <span className={`dw-tab__dot dw-tab__dot--${b.kind}`} aria-hidden />{b.name}
                  </button>
                ))}
              </div>
            </>,
            document.body,
          )}
        </span>
      )}

      {sorting && (
        <span className="dwx-sorthint">
          <ArrowUpDown size={12} aria-hidden />
          탭을 끌어 순서를 바꾸세요 · 이 순서는 나에게만 적용됩니다
          <button type="button" className="dw-btn dw-btn--xs" onClick={() => setSorting(false)}>끝</button>
        </span>
      )}
    </div>
  );
}
